/**
 * @file Handles toggle and close behavior for a UI side panel component,
 * including closing the panel when clicking outside or using a dedicated close button.
 * Author: Dreamers of Day
 */

/**
 * Initializes side panel toggle and close interactions.
 *
 * - Toggles the panel with the `.js-side-panel-button`
 * - Closes the panel when clicking outside of it
 * - Closes the panel when `.js-side-panel-close-button` is clicked
 * - Auto‑opens once after a delay, but never again after user closes (per session)
 *
 * @returns {void}
 */
function sidePanelA() {
    // Key for remembering that the panel was closed this session
    const PANEL_CLOSED_KEY = 'sidePanelClosed';

    // Select the side panel DOM node
    const panel = document.querySelector('.js-side-panel');

    // Select the main toggle button DOM node
    const button = document.querySelector('.js-side-panel-button');

    // Select all DOM nodes marked to explicitly close the panel
    const closeButtons = document.querySelectorAll(
        '.js-side-panel-close-button',
    );

    // Defensive programming: Ensure required nodes exist before continuing
    if (!panel || !button) {
        // If either panel or button are missing, exit the function early
        return;
    }

    // Check sessionStorage: did the user already close the panel?
    const wasClosed = sessionStorage.getItem(PANEL_CLOSED_KEY) === 'true';

    // ---- MANUAL TOGGLE & CLOSE HANDLERS ----

    // Attach click listener to toggle button
    button.addEventListener('click', (event) => {
        // Prevent event from bubbling up to parent elements like document
        event.stopPropagation();

        // Toggle the active state class on the panel
        // This class typically controls panel visibility via CSS
        panel.classList.toggle('m-side-panel--active');
    });

    // Common close handler: hides panel and remembers closure
    const handleClose = (event) => {
        // Prevent bubbling (in case inside link or nested element)
        event.stopPropagation();
        panel.classList.remove('m-side-panel--active');
        // Remember that the user closed the panel, so we never auto-open again this session
        sessionStorage.setItem(PANEL_CLOSED_KEY, 'true');
        // Cancel the pending auto‑open so it never fires again
        clearTimeout(autoOpenTimeoutId);
    };

    // Attach close handler to each explicit close button
    closeButtons.forEach((closeBtn) => {
        closeBtn.addEventListener('click', handleClose);
    });

    // Close when clicking anywhere outside panel AND button
    document.addEventListener('click', (event) => {
        const clickedOutsidePanel = !panel.contains(event.target);
        const clickedOutsideButton = !button.contains(event.target);

        // If both conditions are met, then user clicked outside the interactive area
        if (clickedOutsidePanel && clickedOutsideButton) {
            // Close the panel by removing the active class
            panel.classList.remove('m-side-panel--active');
            sessionStorage.setItem(PANEL_CLOSED_KEY, 'true');
            clearTimeout(autoOpenTimeoutId);
        }
    });

    // ---- AUTO‑OPEN ONCE AFTER DELAY ----

    // Delay before first auto‑open (in ms)
    const AUTO_OPEN_DELAY = 15 * 1000; // 15 seconds
    let autoOpenTimeoutId;

    // Schedule one‑time auto‑open; store id so we can cancel it
    if (!wasClosed) {
        autoOpenTimeoutId = setTimeout(() => {
            // Only auto-open if it's still closed
            if (!panel.classList.contains('m-side-panel--active')) {
                button.click(); // Programmatically trigger the toggle
            }
        }, AUTO_OPEN_DELAY);
    }
}

// Export the function for use in other parts of the application or modules
export default sidePanelA;
